import { useEffect, useState } from "react";
import { Package as PackageIcon, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatMoney } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { PackageDialog } from "@/components/admin/PackageDialog";
import { ConfirmDelete } from "@/components/admin/shared";
import { AdminHeader, EmptyState } from "./AdminLayout";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

export default function Packages() {
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState({ open: false, item: null });
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/admin/packages").then((r) => setItems(r.data)).catch((e) => { setItems([]); err(e); });
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    if (dialog.item) await api.put(`/admin/packages/${dialog.item.id}`, payload);
    else await api.post("/admin/packages", payload);
    toast.success(dialog.item ? "Package updated" : "Package added");
    setDialog({ open: false, item: null });
    load();
  };

  const toggle = async (p, visible) => {
    try {
      await api.put(`/admin/packages/${p.id}`, { name: p.name, description: p.description, price: p.price, billing: p.billing, visible });
      setItems((xs) => xs.map((x) => (x.id === p.id ? { ...x, visible } : x)));
    } catch (e) { err(e); }
  };

  const remove = async () => {
    try { await api.delete(`/admin/packages/${toDelete.id}`); toast.success("Package deleted"); setToDelete(null); load(); } catch (e) { err(e); }
  };

  const add = (
    <button onClick={() => setDialog({ open: true, item: null })} data-testid="admin-add-package-button" className="btn-glow inline-flex items-center gap-2 rounded-full bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-[#07090E] hover:bg-cyan-300">
      <Plus className="h-4 w-4" /> New package
    </button>
  );

  return (
    <div data-testid="admin-packages-page">
      <AdminHeader eyebrow="// pricing" title="Packages" action={add}>Service packages shown under Packages & Pricing on the Services page.</AdminHeader>
      {items === null ? <div className="h-40 animate-pulse rounded-2xl bg-white/[0.03]" /> : items.length === 0 ? (
        <EmptyState icon={PackageIcon} title="No packages yet" text="Add a package to show it on the Services page." testId="admin-packages-empty-state" />
      ) : (
        <div data-testid="admin-packages-list" className="rounded-2xl border border-white/5 bg-[#0B0F18]">
          {items.map((p) => (
            <div key={p.id} data-testid={`admin-package-row-${p.id}`} className="flex flex-col gap-3 border-b border-white/5 p-4 last:border-0 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-white">{p.name}</p>
                <p className="mt-1 line-clamp-1 text-xs text-slate-500">{p.description}</p>
              </div>
              <p className="font-mono-tech text-sm text-cyan-200">{formatMoney(p.price)}{p.billing === "monthly" ? "/month" : ""}</p>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs text-slate-400">
                  <Switch checked={p.visible} onCheckedChange={(v) => toggle(p, v)} data-testid={`package-visible-toggle-${p.id}`} /> {p.visible ? "Visible" : "Hidden"}
                </label>
                <button onClick={() => setDialog({ open: true, item: p })} data-testid={`package-edit-button-${p.id}`} aria-label="Edit" className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => setToDelete(p)} data-testid={`package-delete-button-${p.id}`} aria-label="Delete" className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <PackageDialog open={dialog.open} item={dialog.item} onClose={() => setDialog({ open: false, item: null })} onSave={save} />
      <ConfirmDelete target={toDelete} label={toDelete?.name} onCancel={() => setToDelete(null)} onConfirm={remove} />
    </div>
  );
}
